import React from "react";

// @mui material components
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Services
import MongoHandler from "services/mongoHandler";
import TokenFunctions from "functions/tokenFunctions";
import { useNavigate } from 'react-router-dom';

function Ratings() {
    const mongoHandler = new MongoHandler();
    const tokenFunctions = new TokenFunctions();
    const navigate = useNavigate();

    const [ratingList, setRatingList] = React.useState([]);
    const [mongoRating, setMongoRating] = React.useState([]);
    const [loading, setLoading] = React.useState(true); // Loading state
    const [timeoutReached, setTimeoutReached] = React.useState(false); // Timeout state
    const [dialogOpen, setDialogOpen] = React.useState(false); // State to manage dialog visibility
    const [deleteIndex, setDeleteIndex] = React.useState(null); // To track which row to delete

    const productURL = process.env.REACT_APP_SHOP_URL + "produkt/";

    const onPageLoad = async () => {
        try {
            const ratingListTemp = await mongoHandler.getAllRatings();
            if (ratingListTemp) {
                setRatingList(transformReviewsToTable(ratingListTemp));
                setMongoRating(ratingListTemp);
            }
            if (!await tokenFunctions.refreshToken()) {
                navigate('/authentication/sign-in/simple');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false); // Stop the spinner after fetching
        }
    };

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setTimeoutReached(true);
        }, 10000); // 10 seconds timeout

        onPageLoad();

        return () => clearTimeout(timeout); // Cleanup the timeout on component unmount
    }, []);

    async function handleToggleEnable(rowIndex, value) {
        let updatedRows = [];

        setRatingList((prevRatingList) => {
            updatedRows = [...prevRatingList.rows];
            updatedRows[rowIndex].enabled = value ? <MDBadge badgeContent="Genehmigt" container color="success" /> : <MDBadge badgeContent="Abgelenht" container color="error" />;
            return { ...prevRatingList, rows: updatedRows };
        });

        const update = {
            "enabled": value ? true : false,
        };

        try {            
            await mongoHandler.updateRating(updatedRows[rowIndex].id, update);
            await calcMeanRatingOfProduct(updatedRows[rowIndex].productId.props.href.split('/').pop(), updatedRows);
        } catch (e) {
            console.error(e);
        }
    }

    async function calcMeanRatingOfProduct(id, updatedList){
        let sum = 0;
        let count = 0;
        for (let i = 0; i < updatedList.length; i++) {
            if(updatedList[i].enabled.props.color === "success" && updatedList[i].productId.props.href.split('/').pop() === id){
                console.log(updatedList[i].rate);
                sum = sum + updatedList[i].rate;
                count += 1;
            } else {

            }
        }
        let mean = sum / count;
        if(mean === NaN){
            mean = 0;
        }
        const body ={
            "rating": {
                "totalCount": count,
                "mean": mean
            }
        }
        await mongoHandler.updateProduct(id, body);
    }

    async function deleteRating() {
        if (deleteIndex === null) return;

        let updatedRows = [];
        let idTemp = 0;

        setRatingList((prevRatingList) => {
            updatedRows = [...prevRatingList.rows];
            idTemp = updatedRows[deleteIndex].id;
            updatedRows.splice(deleteIndex, 1);
            return { ...prevRatingList, rows: updatedRows };
        });

        try {
            const response = await mongoHandler.deleteRating(idTemp);
            console.log(response);
        } catch (e) {
            console.error(e);
        } finally {
            setDialogOpen(false); // Close the dialog after deletion
            setDeleteIndex(null); // Reset the delete index
        }
    }

    function transformReviewsToTable(data) {
        if (!data || !data.map) return { columns: [], rows: [] }; // Prevent errors on invalid data

        return {
            columns: [
                { Header: "Aktionen", accessor: "actions" }, // New column for actions
                { Header: "Status", accessor: "enabled" },
                { Header: "Produkt", accessor: "productId" },
                { Header: "Bewertung", accessor: "rate" },
                {
                    Header: "Kommentar",
                    accessor: "comment",
                    width: "25%",                           // Thinner width
                },
                { Header: "Datum", accessor: "date" },
                { Header: "Title", accessor: "title" },
                { Header: "Ort", accessor: "ort" },
                { Header: "ID", accessor: "id" },
            ],

            rows: data.map((item, index) => {
                // Format the date to "DD.MM.YYYY"
                let date = item.date?.$date || item.date || 0;
                let formattedDate = new Date(date);
                let day = String(formattedDate.getDate()).padStart(2, '0');
                let month = String(formattedDate.getMonth() + 1).padStart(2, '0');
                let year = formattedDate.getFullYear();
                let formattedDateString = `${day}.${month}.${year}`;

                return {
                    productId: <a href={productURL + item.productId} target="_blank">Produkt Link</a> || "",
                    ort: item.ort || "",
                    title: item.title || "",
                    date: formattedDateString,  // Use the formatted date here
                    rate: item.rate || "",
                    id: item._id || "",
                    enabled: item.enabled ? <MDBadge badgeContent="Genehmigt" container color="success" /> : <MDBadge badgeContent="Abgelenht" container color="error" />,
                    comment: (
                        <div style={{ width: "500px", height: "100px", overflow: "auto" }}>
                            {item.comment ? item.comment : ""}
                        </div>
                    ),
                    actions: (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleToggleEnable(index, true)}
                                sx={{ color: "green" }}
                            >
                                Erlauben
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleToggleEnable(index, false)}
                                sx={{ ml: 1, color: "red" }}
                            >
                                Ablehnen
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => {
                                    setDeleteIndex(index); // Set the index to delete
                                    setDialogOpen(true); // Open the confirmation dialog
                                }}
                                sx={{ ml: 1, color: "error" }}
                            >
                                <DeleteIcon />
                            </Button>
                        </>
                    ),
                };
            }),
        };
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={3} lineHeight={1}>
                            <MDTypography variant="h5" fontWeight="medium">
                                Bewertungen
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                                Überblick über Bewertungen und Kommentare
                            </MDTypography>
                        </MDBox>
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                                <CircularProgress />
                            </MDBox>
                        ) : timeoutReached && ratingList.rows.length === 0 ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                                <MDTypography variant="h6" color="text">
                                    Keine Bewertungen erhalten
                                </MDTypography>
                            </MDBox>
                        ) : (
                            ratingList && <DataTable table={ratingList} />
                        )}
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />

            {/* Confirmation Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Sind Sie sicher?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Möchten Sie diese Bewertung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={deleteRating}
                        variant="contained"
                        color="success"
                        size="small"
                        autoFocus
                        sx={{ ml: 1, color: "green" }}
                        style={{ marginRight: "auto" }}>
                        Ja
                    </Button>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        variant="contained"
                        color="error"
                        size="small"
                        sx={{ mr: 1, color: "red" }}
                        style={{ marginLeft: "auto" }}>
                        Nein
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default Ratings;